// The follow-ups under Neo's newest answer (TOM-709, contract §1): up to three
// plain-text chips, each a real 44 px target. A question chip sends its label
// as the owner's message, naming the answer and chip it came from, and leaves
// the chat box's draft and its photos and files alone; recalculate and drill
// down send the same way (the server applies them). "Show the evidence" opens
// Behind the answer (W2, TOM-710) and is drawn only where that exists; a chip
// that switches a chart's view calls the hook W3 (TOM-711) fills.

import type { NeoFollowup } from '@neomaris/protocol';

import { neoVisibleChips } from '../../lib/neoAnswers';

/**
 * What a chip that is not a question does, given by the chat that draws the
 * answer. W3 fills `showView`; W2 gives `inspectEvidence` where Behind the
 * answer exists, and without it no "Show the evidence" chip is drawn.
 */
export interface NeoAnswerViewHooks {
  /** Switch the chart card `groupId` of answer `answerId` to its view `recordId`. */
  showView?: (answerId: string, groupId: string, recordId: string) => void;
  /** Open Behind the answer on answer `answerId`, at one evidence item when named. */
  inspectEvidence?: (answerId: string, evidenceId?: string) => void;
}

export function NeoFollowups({
  followups,
  disabled,
  onSend,
  onShowView,
  onInspect,
}: {
  followups: readonly NeoFollowup[];
  /** True while a turn is on its way or the chat is still being read. */
  disabled: boolean;
  /** Ask Neo with the chip's label; `index` is its place in the answer's follow-ups. */
  onSend: (label: string, index: number) => void;
  /** Absent where the chat cannot switch a chart's view: no view chip then. */
  onShowView?: (groupId: string, recordId: string) => void;
  /** Absent where there is no Behind the answer: an evidence chip asks Neo instead. */
  onInspect?: (evidenceId?: string) => void;
}) {
  const chips = neoVisibleChips(followups, {
    evidence: onInspect !== undefined,
    view: onShowView !== undefined,
  });
  if (chips.length === 0) return null;
  return (
    <div
      className="neo-answer__followups"
      role="group"
      aria-label="Ask next"
      data-testid="neo-followups"
    >
      {chips.map(({ followup, index, action }) => (
        <button
          key={index}
          type="button"
          className="neo-answer__followup"
          data-intent={followup.intent.kind}
          disabled={disabled && action === 'send'}
          onClick={() => {
            const intent = followup.intent;
            if (action === 'view' && intent.kind === 'show_view') {
              onShowView?.(intent.groupId, intent.recordId);
            } else if (action === 'evidence' && intent.kind === 'inspect_evidence') {
              onInspect?.(intent.evidenceId);
            } else onSend(followup.label, index);
          }}
        >
          {followup.label}
        </button>
      ))}
    </div>
  );
}
